import type { ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { ApiError } from '@/lib/api'
import { formatDecimal } from '@/lib/format'
import { StateView } from '@/card/record/StateView'
import type { Period } from './types'

/**
 * Общая обвязка экранов отчётов (ТЗ §4.6) — тикет #76.
 *
 * Восемь готовых отчётов делят одно и то же: заголовок, выбор периода, состояния загрузки
 * и отказа, таблица результата. Написать это восемь раз значило бы получить восемь
 * немного разных ответов на вопрос «что показать, когда права нет», и разошлись бы они
 * при первой правке.
 */

/** Выбор периода: две даты. */
export function PeriodPicker({
  value,
  onChange,
}: {
  value: Period
  onChange: (next: Period) => void
}) {
  const { t } = useApp()
  return (
    <div className="report-period">
      <label>
        {t('report.period.from')}
        <input
          type="date"
          value={value.from}
          onChange={(event) => onChange({ ...value, from: event.target.value })}
        />
      </label>
      <label>
        {t('report.period.to')}
        <input
          type="date"
          value={value.to}
          onChange={(event) => onChange({ ...value, to: event.target.value })}
        />
      </label>
    </div>
  )
}

/**
 * Состояние запроса отчёта одним местом.
 *
 * Отказ по правам показывается своим текстом, а не общей ошибкой: «нет доступа» и «что-то
 * сломалось» — разные сообщения, и второе отправляет пользователя писать в поддержку о
 * запрете, который работает как задумано.
 */
export function ReportState({
  isLoading,
  error,
  isEmpty,
  empty,
  children,
}: {
  isLoading: boolean
  error: unknown
  isEmpty: boolean
  /**
   * Что сказать о пустоте на этом экране (#92). По умолчанию — «за период данных нет», и
   * это верно только там, где период есть: на экране без периода такая фраза отправляет
   * искать фильтр, которого не существует.
   */
  empty?: ReactNode
  children: ReactNode
}) {
  const { t } = useApp()

  if (isLoading) return <p>{t('app.loading')}</p>
  if (error) {
    const forbidden = error instanceof ApiError && error.isForbidden
    // 422 с кодом отказа по источнику — это тоже запрет, просто пойманный глубже:
    // право на раздел есть, права на данные нет.
    const forbiddenSource =
      error instanceof ApiError && error.code === 'report.forbidden_source'
    if (forbidden || forbiddenSource) {
      // A refusal, not an emptiness: the two must not look alike (#92).
      return <StateView compact kind="forbidden" title={t('access.denied')} />
    }
    return (
      <StateView
        compact
        kind="error"
        title={error instanceof ApiError ? error.message : t('app.error')}
      />
    )
  }
  if (isEmpty) return <>{empty ?? <StateView compact kind="empty" title={t('report.empty')} />}</>
  return <>{children}</>
}

/** Каркас экрана отчёта: заголовок, панель параметров, тело. */
export function ReportPage({
  titleKey,
  hintKey,
  controls,
  children,
}: {
  titleKey: string
  hintKey?: string
  controls?: ReactNode
  children: ReactNode
}) {
  const { t } = useApp()
  return (
    <section className="report-page">
      <header className="list-header">
        <h1>{t(titleKey)}</h1>
        {controls && <div className="list-actions">{controls}</div>}
      </header>
      {hintKey && <p className="muted report-hint">{t(hintKey)}</p>}
      {children}
    </section>
  )
}

/**
 * Значение ячейки отчёта.
 *
 * `null` рисуется прочерком, а не нулём и не пустотой: у отчётов этого проекта `null`
 * значим — маржа без себестоимости, CAC без затрат, конверсия непройденной стадии. Ноль
 * на его месте — это утверждение, которого никто не делал.
 */
export function Value({
  value,
  suffix,
}: {
  value: string | number | null | undefined
  suffix?: string
}) {
  const { locale } = useApp()
  if (value === null || value === undefined || value === '') {
    return <span className="muted" title="нет данных">—</span>
  }
  const numeric = typeof value === 'number' ? value : Number(value)
  const text = Number.isFinite(numeric)
    ? // Дробное — всегда два знака: «5 293 714,5» под «5 292 347,66» читается как
      // другая точность. Валюты у строки отчёта нет — суммой с символом не печатаем (#91).
      formatDecimal(numeric, Number.isInteger(numeric) ? 0 : 2, locale)
    : String(value)
  return (
    <>
      {text}
      {suffix ? ` ${suffix}` : ''}
    </>
  )
}

/**
 * Доля в виде полоски.
 *
 * Числа в таблице сравниваются глазами плохо, а отчёт существует ради сравнения. Полоска
 * рисуется чистым CSS и не тянет библиотеку графиков: одно измерение, одна величина —
 * это не тот случай, ради которого её заводят.
 */
export function Bar({ percent, label }: { percent: number | null; label?: string }) {
  if (percent === null || !Number.isFinite(percent)) {
    return <span className="muted">—</span>
  }
  const clamped = Math.max(0, Math.min(100, percent))
  return (
    <span className="report-bar" title={label ?? `${clamped.toFixed(1)}%`}>
      <span className="report-bar-fill" style={{ width: `${clamped}%` }} />
      <span className="report-bar-label">{clamped.toFixed(1)}%</span>
    </span>
  )
}
